<script setup lang="ts">
import { computed } from 'vue';
import { useAudioPlayer } from '@/composables/useAudioPlayer';

interface Props {
  size?: 'mini' | 'compact' | 'default' | 'large';
  showShuffle?: boolean;
  showRepeat?: boolean;
  showPrevious?: boolean;
  showNext?: boolean;
  disableShuffle?: boolean;
  disablePrevious?: boolean;
  disableNext?: boolean;
  disableRepeat?: boolean;
  playButtonVariant?: 'text' | 'flat' | 'elevated';
  playButtonColor?: string;
  iconColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'default',
  showShuffle: true,
  showRepeat: true,
  showPrevious: true,
  showNext: true,
  disableShuffle: false,
  disablePrevious: false,
  disableNext: false,
  disableRepeat: false,
  playButtonVariant: 'flat',
  playButtonColor: 'primary',
  iconColor: undefined,
});

const emit = defineEmits<{
  (e: 'toggle-play'): void;
  (e: 'previous'): void;
  (e: 'next'): void;
  (e: 'toggle-shuffle'): void;
  (e: 'toggle-repeat'): void;
}>();

const audioPlayer = useAudioPlayer();

const isPlaying = computed(() => audioPlayer.isPlaying.value);
const isShuffleEnabled = computed(() => audioPlayer.isShuffleEnabled.value);
const repeatMode = computed(() => audioPlayer.repeatMode.value);

// Icon sizes based on component size
const iconSizes = computed(() => {
  switch (props.size) {
    case 'mini':
      return { shuffle: 18, nav: 22, play: 28, repeat: 18 };
    case 'compact':
      return { shuffle: 20, nav: 24, play: 32, repeat: 20 };
    case 'large':
      return { shuffle: 28, nav: 36, play: 48, repeat: 28 };
    default:
      return { shuffle: 24, nav: 28, play: 36, repeat: 24 };
  }
});

// Button sizes based on component size
const buttonSizes = computed(() => {
  switch (props.size) {
    case 'mini':
      return { control: 'small', play: 'default' };
    case 'compact':
      return { control: 'small', play: 'default' };
    case 'large':
      return { control: 'large', play: 'x-large' };
    default:
      return { control: 'small', play: 'default' };
  }
});

const repeatIcon = computed(() => {
  if (repeatMode.value === 'one') return 'mdi-repeat-once';
  if (repeatMode.value === 'all') return 'mdi-repeat';
  return 'mdi-repeat-off';
});

function handleTogglePlay() {
  audioPlayer.togglePlay();
  emit('toggle-play');
}

function handlePrevious() {
  audioPlayer.playPrevious();
  emit('previous');
}

function handleNext() {
  audioPlayer.skipNext();
  emit('next');
}

function handleToggleShuffle() {
  audioPlayer.toggleShuffle();
  emit('toggle-shuffle');
}

function handleToggleRepeat() {
  audioPlayer.toggleRepeat();
  emit('toggle-repeat');
}
</script>

<template>
  <div class="playback-controls" :class="`playback-controls--${size}`">
    <!-- Shuffle -->
    <v-btn
      v-if="showShuffle"
      icon
      variant="text"
      :size="buttonSizes.control"
      :color="isShuffleEnabled ? 'primary' : undefined"
      :disabled="disableShuffle"
      aria-label="Toggle shuffle"
      @click="handleToggleShuffle"
    >
      <v-icon :size="iconSizes.shuffle">
        mdi-shuffle{{ isShuffleEnabled ? '' : '-disabled' }}
      </v-icon>
    </v-btn>

    <!-- Previous -->
    <v-btn
      v-if="showPrevious"
      :icon="size !== 'large'"
      variant="text"
      :size="buttonSizes.control"
      :color="iconColor"
      :disabled="disablePrevious"
      :class="{ 'playback-controls__nav-btn': size === 'large' }"
      aria-label="Previous track"
      @click="handlePrevious"
    >
      <v-icon :size="iconSizes.nav">mdi-skip-previous</v-icon>
    </v-btn>

    <!-- Play/Pause -->
    <v-btn
      :icon="size !== 'large'"
      :variant="playButtonVariant"
      :color="playButtonColor"
      :size="buttonSizes.play"
      class="playback-controls__play-btn"
      aria-label="Toggle play/pause"
      @click="handleTogglePlay"
    >
      <v-icon :size="iconSizes.play">
        {{ isPlaying ? 'mdi-pause' : 'mdi-play' }}
      </v-icon>
    </v-btn>

    <!-- Next -->
    <v-btn
      v-if="showNext"
      :icon="size !== 'large'"
      variant="text"
      :size="buttonSizes.control"
      :color="iconColor"
      :disabled="disableNext"
      :class="{ 'playback-controls__nav-btn': size === 'large' }"
      aria-label="Next track"
      @click="handleNext"
    >
      <v-icon :size="iconSizes.nav">mdi-skip-next</v-icon>
    </v-btn>

    <!-- Repeat -->
    <v-btn
      v-if="showRepeat"
      icon
      variant="text"
      :size="buttonSizes.control"
      :color="repeatMode !== 'off' ? 'primary' : undefined"
      :disabled="disableRepeat"
      aria-label="Toggle repeat"
      @click="handleToggleRepeat"
    >
      <v-icon :size="iconSizes.repeat">{{ repeatIcon }}</v-icon>
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
.playback-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  &--mini {
    gap: 2px;
  }

  &--compact {
    gap: 4px;
  }

  &--default {
    gap: 8px;
  }

  &--large {
    gap: 16px;

    .playback-controls__play-btn {
      border-radius: 16px !important;
      min-width: 72px !important;
      height: 72px !important;
      padding: 0 !important;

      &:hover {
        opacity: 0.85;
      }
    }

    .playback-controls__nav-btn {
      border-radius: 14px !important;
      min-width: 56px !important;
      height: 56px !important;
      padding: 0 !important;
      background: rgba(255, 255, 255, 0.08) !important;

      &:hover {
        background: rgba(255, 255, 255, 0.15) !important;
      }
    }

    // Active state for shuffle/repeat
    .v-btn.v-btn--active,
    .v-btn[style*="color: rgb(var(--v-theme-primary))"] {
      background: rgb(var(--v-theme-primary)) !important;

      :deep(.v-icon) {
        color: white !important;
      }
    }
  }
}
</style>
